import { CodeFile, type EditorError } from 'mini-code'
import { useCallback, useEffect, useRef } from 'react'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import type { BytecodeProgram } from './bytecode.ts'
import { dspscript } from './dsp-script.ts'
import { convertToEditorErrors } from './editor-errors.ts'
import {
  useChannelModeStore,
  useCompileModeStore,
  useCrossfadeStore,
  useEditorErrorsStore,
  useGlobalBpmStore,
  useSessionStore,
} from './store.ts'
import { normalizeWhitespace } from './util.ts'

const compiler = new BytecodeCompiler()

interface AutoCompileManagerProps {
  codeFileA: CodeFile
  codeFileB: CodeFile
  activeTabId: string
  playingTabId: string | null
  onCompile?: (
    codeA: string,
    codeB: string,
    programA: BytecodeProgram,
    programB?: BytecodeProgram,
  ) => void
  onClearError?: () => void
  onShowError?: (message: string) => void
  trackLoadingTimeoutRef?: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
  onCompilingChange?: (isCompiling: boolean) => void
}

export function AutoCompileManager({
  codeFileA,
  codeFileB,
  activeTabId,
  playingTabId,
  onCompile,
  onClearError,
  onShowError,
  trackLoadingTimeoutRef,
  onCompilingChange,
}: AutoCompileManagerProps) {
  const { compileMode } = useCompileModeStore()
  const { channelMode } = useChannelModeStore()
  const { globalBpm } = useGlobalBpmStore()
  // Use getState to avoid subscribing and causing re-renders
  const getEditorErrorsStore = useEditorErrorsStore.getState
  const getEditorInitiatedPlayback = useSessionStore(state => state.getEditorInitiatedPlayback)
  const crossfade = useCrossfadeStore(state => state.crossfade)
  const compileModeRef = useRef(compileMode)

  // Ensure compileModeRef is always current
  useEffect(() => {
    compileModeRef.current = compileMode
  }, [compileMode])

  const compileTimeoutRefA = useRef<ReturnType<typeof setTimeout> | null>(null)
  const compileTimeoutRefB = useRef<ReturnType<typeof setTimeout> | null>(null)
  const errorCheckTimeoutRefA = useRef<ReturnType<typeof setTimeout> | null>(null)
  const errorCheckTimeoutRefB = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSeenCodeA = useRef(codeFileA.value)
  const lastSeenCodeB = useRef(codeFileB.value)
  // Initialize to empty string - only update when compilation succeeds
  // This ensures errors are always checked on initial load
  const lastCompiledCodeA = useRef('')
  const lastCompiledCodeB = useRef('')
  // Track when codeFile reference changes to prevent subscription from setting errors prematurely
  const codeFileJustChangedRef = useRef(false)
  // Track current compilation ID to ignore stale compilations
  const currentCompilationIdA = useRef(0)
  const currentCompilationIdB = useRef(0)
  // Track compilation epoch - increment on each compile request, never clear timeouts
  // Use a single shared epoch for both A and B to prevent double-incrementing
  const compileEpoch = useRef(0)
  // Track effect generation - increment when effect re-runs to invalidate old compilations
  const effectGeneration = useRef(0)
  // Store callbacks in refs to avoid dependency issues
  const onClearErrorRef = useRef(onClearError)
  const onShowErrorRef = useRef(onShowError)
  const onCompileRef = useRef(onCompile)
  const onCompilingChangeRef = useRef(onCompilingChange)

  // Update refs when callbacks change
  useEffect(() => {
    onClearErrorRef.current = onClearError
    onShowErrorRef.current = onShowError
    onCompileRef.current = onCompile
    onCompilingChangeRef.current = onCompilingChange
    compileModeRef.current = compileMode
  }, [onClearError, onShowError, onCompile, onCompilingChange, compileMode])

  // Robust error checking - always validates code and sets/clears errors accordingly
  // Use useCallback to ensure the function always has the latest dependencies
  const checkForErrors = useCallback(
    (codeA: string, codeB: string): void => {
      // Get current code to verify we're checking the right version
      const currentCodeA = codeFileA.value
      const currentCodeB = codeFileB.value
      const normalizedCodeA = normalizeWhitespace(codeA)
      const normalizedCodeB = normalizeWhitespace(codeB)
      const normalizedCurrentA = normalizeWhitespace(currentCodeA)
      const normalizedCurrentB = normalizeWhitespace(currentCodeB)

      // Only update errors if the code being checked matches the current code
      // This prevents clearing errors for code that has changed
      const codeMatches = normalizedCodeA === normalizedCurrentA
        && (channelMode === 'single' || normalizedCodeB === normalizedCurrentB)

      // Always get fresh setters to ensure store updates trigger re-renders
      const store = useEditorErrorsStore.getState()
      let firstError: string | null = null
      let hasErrors = false
      let newErrorsA: EditorError[] | null = null
      let newErrorsB: EditorError[] | null = null

      // Each compile() call resets the compiler state, so concurrent compilations are safe
      // However, we serialize to ensure consistent error reporting
      if (channelMode === 'dual') {
        // Check channel A
        try {
          compiler.compile(codeA)
          // Only clear errors if code matches current code
          if (codeMatches) {
            newErrorsA = []
          }
        }
        catch (errA: any) {
          hasErrors = true
          const editorErrors = convertToEditorErrors(errA, codeA)
          // Always set errors if found
          newErrorsA = editorErrors
          if (!firstError && editorErrors[0]?.message) {
            firstError = editorErrors[0].message
          }
        }

        // Check channel B
        try {
          compiler.compile(codeB)
          // Only clear errors if code matches current code
          if (codeMatches) {
            newErrorsB = []
          }
        }
        catch (errB: any) {
          hasErrors = true
          const editorErrors = convertToEditorErrors(errB, codeB)
          // Always set errors if found
          newErrorsB = editorErrors
          if (!firstError && editorErrors[0]?.message) {
            firstError = editorErrors[0].message
          }
        }
      }
      else {
        // Single mode - only check channel A
        try {
          compiler.compile(codeA)
          // Only clear errors if code matches current code
          if (codeMatches) {
            newErrorsA = []
          }
        }
        catch (errA: any) {
          hasErrors = true
          const editorErrors = convertToEditorErrors(errA, codeA)
          // Always set errors if found
          newErrorsA = editorErrors
          if (editorErrors[0]?.message) {
            firstError = editorErrors[0].message
          }
        }
      }

      // Update store synchronously to ensure re-renders are triggered
      if (newErrorsA !== null) {
        store.setErrorsA(newErrorsA)
      }
      if (newErrorsB !== null) {
        store.setErrorsB(newErrorsB)
      }

      // Update TabBar error state based on compilation results
      // Always show errors if found, but only clear if code matches
      if (hasErrors && firstError) {
        onShowErrorRef.current?.(firstError)
      }
      else if (!hasErrors && codeMatches) {
        // Only clear errors if code matches to avoid clearing errors for code that has changed
        onClearErrorRef.current?.()
      }
    },
    [channelMode, codeFileA, codeFileB],
  )

  const checkForErrorsRef = useRef<((codeA: string, codeB: string) => void) | null>(null)
  const handleCompileRef = useRef<
    ((codeA: string, codeB: string) => { success: boolean; loadingPromise?: Promise<void> }) | null
  >(null)

  // Update ref when checkForErrors changes
  useEffect(() => {
    checkForErrorsRef.current = checkForErrors
  }, [checkForErrors])

  // Update refs when codeFile props change (e.g., when switching tabs or restoring from history)
  useEffect(() => {
    // Mark that codeFile just changed - this prevents subscriptions from setting errors
    codeFileJustChangedRef.current = true

    // Clear loading state when switching tabs/programs
    onCompilingChangeRef.current?.(false)

    // Clear errors IMMEDIATELY and SYNCHRONOUSLY when codeFile changes
    // This must happen before any subscriptions can set new errors
    // But don't clear if a track is being loaded (let the track loading handle it)
    if (!trackLoadingTimeoutRef?.current) {
      onClearErrorRef.current?.()
      const { setErrorsA, setErrorsB } = getEditorErrorsStore()
      setErrorsA([])
      setErrorsB([])
    }

    const currentCodeA = codeFileA.value
    const currentCodeB = codeFileB.value

    lastSeenCodeA.current = currentCodeA
    lastSeenCodeB.current = currentCodeB
    // Don't update lastCompiledCode here - only update when compilation succeeds
    // This ensures we check for errors even if the loaded code has errors

    // Check errors immediately, then also check after render to ensure UI updates
    // This ensures errors are checked even if requestAnimationFrame is delayed
    checkForErrors(currentCodeA, currentCodeB)

    // Use requestAnimationFrame to reset the flag and ensure UI has updated
    requestAnimationFrame(() => {
      codeFileJustChangedRef.current = false
      // Double-check errors after render to catch any edge cases
      // Re-read values in case they changed
      checkForErrors(codeFileA.value, codeFileB.value)
    })
  }, [codeFileA, codeFileB, checkForErrors])

  // Unified error checking and auto-compile logic
  // This handles all code changes: editing, undo/redo, loading tracks, etc.
  useEffect(() => {
    // Increment effect generation to invalidate any pending compilations from previous effect instance
    effectGeneration.current++
    const currentGeneration = effectGeneration.current
    // Clear loading when effect re-runs to prevent it from getting stuck
    onCompilingChangeRef.current?.(false)

    // Capture checkForErrors in a local variable so subscription callbacks always have access
    const currentCheckForErrors = checkForErrors
    // Get compileMode store getter to read latest value in callbacks
    const getCompileMode = useCompileModeStore.getState

    // Compile immediately for widgets (no loading, just extract widgets and update tab store)
    const compileForWidgets = (codeA: string, codeB: string): void => {
      try {
        if (channelMode === 'dual') {
          let programA
          let programB
          try {
            programA = compiler.compile(codeA)
          } catch {
            return // Errors will be handled by error checking
          }
          try {
            programB = compiler.compile(codeB)
          } catch {
            return // Errors will be handled by error checking
          }
          // Update widgets in tab store immediately
          onCompileRef.current?.(codeA, codeB, programA, programB)
        } else {
          let program
          try {
            program = compiler.compile(codeA)
          } catch {
            return // Errors will be handled by error checking
          }
          // Update widgets in tab store immediately
          onCompileRef.current?.(codeA, codeB, program)
        }
      } catch {
        // Silently ignore errors - they'll be caught by error checking
      }
    }

    // Define handleCompile inside the effect so it always has the latest values
    const handleCompile = (codeA: string, codeB: string): { success: boolean; loadingPromise?: Promise<void> } => {
      const { setErrorsA, setErrorsB } = getEditorErrorsStore()
      try {
        if (channelMode === 'dual') {
          let programA
          let programB

          try {
            programA = compiler.compile(codeA)
          }
          catch (errA: any) {
            const editorErrors = convertToEditorErrors(errA, codeA)
            setErrorsA(editorErrors)
            setErrorsB([])
            return { success: false }
          }

          try {
            programB = compiler.compile(codeB)
          }
          catch (errB: any) {
            const editorErrors = convertToEditorErrors(errB, codeB)
            setErrorsA([])
            setErrorsB(editorErrors)
            return { success: false }
          }

          // Only clear errors if the compiled code matches the current code
          // This prevents clearing errors that were set for newer code
          const currentCodeA = codeFileA.value
          const currentCodeB = codeFileB.value
          const normalizedCompiledA = normalizeWhitespace(codeA)
          const normalizedCompiledB = normalizeWhitespace(codeB)
          const normalizedCurrentA = normalizeWhitespace(currentCodeA)
          const normalizedCurrentB = normalizeWhitespace(currentCodeB)

          if (normalizedCompiledA === normalizedCurrentA && normalizedCompiledB === normalizedCurrentB) {
            setErrorsA([])
            setErrorsB([])
            onClearErrorRef.current?.()
          }
          else {
            // Code has changed since compilation started, re-check for errors
            currentCheckForErrors(currentCodeA, currentCodeB)
          }

          // Only reload if audio is playing AND we're in the editor (not from PublicTracks)
          const actualPlaying = dspscript.isPlaying()
          const editorInitiated = getEditorInitiatedPlayback()
          if (actualPlaying && dspscript.getAudioContext() && editorInitiated) {
            // Don't set loading to true here - compilation loading is already active
            // Program loading will continue the loading state
            // Loading state is managed by the caller, not here
            const loadingPromise = dspscript
              .loadDualProgram(codeA, codeB, globalBpm, crossfade)
              .catch((err: any) => {
                const editorErrors = convertToEditorErrors(err, codeA)
                setErrorsA(editorErrors)
                onShowErrorRef.current?.(editorErrors[0]?.message || err.message)
              })
            onCompileRef.current?.(codeA, codeB, programA, programB)
            return { success: true, loadingPromise }
          }
          onCompileRef.current?.(codeA, codeB, programA, programB)
          return { success: true }
        }
        else {
          const program = compiler.compile(codeA)

          // Only clear errors if the compiled code matches the current code
          // This prevents clearing errors that were set for newer code
          const currentCodeA = codeFileA.value
          const normalizedCompiledA = normalizeWhitespace(codeA)
          const normalizedCurrentA = normalizeWhitespace(currentCodeA)

          if (normalizedCompiledA === normalizedCurrentA) {
            setErrorsA([])
            onClearErrorRef.current?.()
          }
          else {
            // Code has changed since compilation started, re-check for errors
            currentCheckForErrors(currentCodeA, codeFileB.value)
          }

          // Only reload if audio is playing AND we're in the editor (not from PublicTracks)
          // AND the active tab is the one that's playing
          const actualPlaying = dspscript.isPlaying()
          const editorInitiated = getEditorInitiatedPlayback()
          const isActiveTabPlaying = activeTabId === playingTabId
          if (actualPlaying && dspscript.getAudioContext() && editorInitiated && isActiveTabPlaying) {
            // Don't set loading to true here - compilation loading is already active
            // Program loading will continue the loading state
            // Loading state is managed by the caller, not here
            const loadingPromise = dspscript
              .loadProgram(codeA)
              .catch((err: any) => {
                const editorErrors = convertToEditorErrors(err, codeA)
                setErrorsA(editorErrors)
                onShowErrorRef.current?.(editorErrors[0]?.message || err.message)
              })
            onCompileRef.current?.(codeA, codeB, program)
            return { success: true, loadingPromise }
          }
          onCompileRef.current?.(codeA, codeB, program)
          return { success: true }
        }
      }
      catch (err: any) {
        const { setErrorsA } = getEditorErrorsStore()
        const editorErrors = convertToEditorErrors(err, codeA)
        setErrorsA(editorErrors)
        return { success: false }
      }
    }
    handleCompileRef.current = handleCompile

    // Reset refs when setting up subscriptions
    // Use normalized comparison to avoid false positives from whitespace-only changes
    const initialCodeA = codeFileA.value
    const initialCodeB = codeFileB.value
    lastSeenCodeA.current = initialCodeA
    lastSeenCodeB.current = initialCodeB

    const unsubscribeA = codeFileA.subscribe(() => {
      const newCode = codeFileA.value
      // Use direct comparison first, then normalized for whitespace-only changes
      const codeChanged = lastSeenCodeA.current !== newCode
      const normalizedNewCode = normalizeWhitespace(newCode)
      const normalizedLastSeen = normalizeWhitespace(lastSeenCodeA.current)
      const normalizedChanged = normalizedLastSeen !== normalizedNewCode

      // If code hasn't actually changed (either directly or after normalization), cancel any pending compiles and return
      if (!codeChanged && !normalizedChanged) {
        if (compileTimeoutRefA.current) {
          clearTimeout(compileTimeoutRefA.current)
          compileTimeoutRefA.current = null
          // Clear loading state if there was a pending compilation
          onCompilingChangeRef.current?.(false)
        }
        return
      }

      lastSeenCodeA.current = newCode

      // Compile immediately for widgets (no delay, no loading)
      // This ensures widgets are updated in sync with keystrokes
      compileForWidgets(newCode, codeFileB.value)

      // Debounce error checking to handle rapid changes (e.g., commenting multiple lines)
      // Cancel any pending error check and schedule a new one
      // Use a longer debounce to ensure it runs even with very rapid changes
      if (errorCheckTimeoutRefA.current) {
        clearTimeout(errorCheckTimeoutRefA.current)
      }
      errorCheckTimeoutRefA.current = setTimeout(() => {
        errorCheckTimeoutRefA.current = null
        // Always read the latest code values when the timeout fires
        // This ensures we check the final state even if code changed multiple times
        const latestCodeA = codeFileA.value
        const latestCodeB = codeFileB.value
        currentCheckForErrors(latestCodeA, latestCodeB)
      }, 100)

      // If codeFile just changed, reset the flag
      if (codeFileJustChangedRef.current) {
        codeFileJustChangedRef.current = false
      }

      // Auto-compile in 'key' mode when code changes
      // Read compileMode directly from store to ensure we have the latest value
      const currentCompileMode = getCompileMode().compileMode
      if (currentCompileMode === 'key') {
        // Increment epoch - this marks a new compilation request
        compileEpoch.current++
        const epoch = compileEpoch.current

        // Capture variables in closure
        const capturedCodeFileA = codeFileA
        const capturedCodeFileB = codeFileB
        const capturedHandleCompile = handleCompileRef.current
        const capturedOnCompilingChange = onCompilingChangeRef.current
        const capturedLastCompiledCodeA = lastCompiledCodeA
        const capturedLastCompiledCodeB = lastCompiledCodeB
        const capturedCompileEpoch = compileEpoch
        const capturedCompileModeRef = compileModeRef
        const capturedEffectGeneration = effectGeneration

        // Create timeout callback - never clear timeouts, just check epoch
        const timeoutCallback = () => {
          // Check if effect has re-run - if so, this compilation is stale
          if (currentGeneration !== capturedEffectGeneration.current) {
            capturedOnCompilingChange?.(false)
            return
          }
          // Check if compileMode has changed - if not 'key', clear loading and return
          if (capturedCompileModeRef.current !== 'key') {
            capturedOnCompilingChange?.(false)
            return
          }
          // Check if this compilation is still current
          if (epoch !== capturedCompileEpoch.current) {
            // A newer compilation has started, clear loading for this stale one
            capturedOnCompilingChange?.(false)
            return
          }
          // Set loading at the start of compilation
          capturedOnCompilingChange?.(true) // Execute async code
          // Safety timeout: clear loading after max duration to prevent it from getting stuck
          const safetyTimeout = setTimeout(() => {
            if (currentGeneration === capturedEffectGeneration.current && epoch === capturedCompileEpoch.current
              && capturedCompileModeRef.current === 'key')
            {
              // Only clear if this compilation is still current, effect hasn't re-run, and mode is still 'key'
              // Otherwise, a newer compilation, effect re-run, or mode change should have handled it
              capturedOnCompilingChange?.(false)
            }
          }, 10000) // 10 second max duration
          ;(async () => {
            try {
              // Check effect generation, compileMode and epoch at start of async code
              if (currentGeneration !== capturedEffectGeneration.current || capturedCompileModeRef.current !== 'key'
                || epoch !== capturedCompileEpoch.current)
              {
                // Clear loading state since compilation is stale, mode changed, or effect re-ran
                clearTimeout(safetyTimeout)
                capturedOnCompilingChange?.(false)
                return
              }
              // Always use the latest code, not the captured code
              // This ensures we compile what the user just typed, even if code changed after scheduling
              const currentCodeA = capturedCodeFileA.value
              const currentCodeB = capturedCodeFileB.value

              if (!capturedHandleCompile) {
                // handleCompile not ready yet, clear loading and return
                clearTimeout(safetyTimeout)
                if (capturedCompileModeRef.current === 'key' && epoch === capturedCompileEpoch.current) {
                  capturedOnCompilingChange?.(false)
                }
                return
              }

              const startTime = Date.now()
              const minLoadingDuration = 300
              // Use latest code instead of captured code
              const result = capturedHandleCompile(currentCodeA, currentCodeB)
              // Check effect generation, compileMode and epoch again after compilation
              if (currentGeneration !== capturedEffectGeneration.current || capturedCompileModeRef.current !== 'key'
                || epoch !== capturedCompileEpoch.current)
              {
                // Clear loading state since compilation is stale, mode changed, or effect re-ran
                clearTimeout(safetyTimeout)
                capturedOnCompilingChange?.(false)
                return
              }
              // Don't update lastSeenCode here - it's already updated in the subscription
              // Updating it here can cause race conditions with new edits
              if (result.success) {
                // Check effect generation, compileMode and epoch before proceeding - if stale, clear loading and return immediately
                if (currentGeneration !== capturedEffectGeneration.current || capturedCompileModeRef.current !== 'key'
                  || epoch !== capturedCompileEpoch.current)
                {
                  clearTimeout(safetyTimeout)
                  capturedOnCompilingChange?.(false)
                  return
                }
                capturedLastCompiledCodeA.current = currentCodeA
                capturedLastCompiledCodeB.current = currentCodeB
                // onCompile is already called by handleCompile when successful
                // Wait for program loading to complete if it was started, with minimum delay
                if (result.loadingPromise) {
                  try {
                    await result.loadingPromise
                    // Check effect generation, compileMode and epoch again after loading
                    if (currentGeneration !== capturedEffectGeneration.current
                      || capturedCompileModeRef.current !== 'key' || epoch !== capturedCompileEpoch.current)
                    {
                      // Clear loading state since compilation is stale, mode changed, or effect re-ran
                      clearTimeout(safetyTimeout)
                      capturedOnCompilingChange?.(false)
                      return
                    }
                  }
                  catch {
                    // Error already handled in handleCompile
                    if (currentGeneration !== capturedEffectGeneration.current
                      || capturedCompileModeRef.current !== 'key' || epoch !== capturedCompileEpoch.current)
                    {
                      // Clear loading state since compilation is stale, mode changed, or effect re-ran
                      clearTimeout(safetyTimeout)
                      capturedOnCompilingChange?.(false)
                      return
                    }
                  }
                }
                // Check effect generation, compileMode and epoch again before scheduling timeout
                if (currentGeneration !== capturedEffectGeneration.current || capturedCompileModeRef.current !== 'key'
                  || epoch !== capturedCompileEpoch.current)
                {
                  clearTimeout(safetyTimeout)
                  capturedOnCompilingChange?.(false)
                  return
                }
                const elapsed = Date.now() - startTime
                const remaining = Math.max(0, minLoadingDuration - elapsed)
                setTimeout(() => {
                  // Final check before clearing loading
                  // Check effect generation, compileMode and epoch
                  clearTimeout(safetyTimeout)
                  if (currentGeneration !== capturedEffectGeneration.current || capturedCompileModeRef.current !== 'key'
                    || epoch === capturedCompileEpoch.current)
                  {
                    capturedOnCompilingChange?.(false)
                  }
                }, remaining)
              }
              else {
                // Compilation failed, clear loading state only if still current
                clearTimeout(safetyTimeout)
                if (currentGeneration === capturedEffectGeneration.current && capturedCompileModeRef.current === 'key'
                  && epoch === capturedCompileEpoch.current)
                {
                  capturedOnCompilingChange?.(false)
                }
              }
            }
            catch (err) {
              // Clear loading state on error if still current
              clearTimeout(safetyTimeout)
              if (currentGeneration === capturedEffectGeneration.current && capturedCompileModeRef.current === 'key'
                && epoch === capturedCompileEpoch.current)
              {
                capturedOnCompilingChange?.(false)
              }
            }
          })()
        }
        // Schedule timeout - never clear it, just let it fire and check epoch
        setTimeout(timeoutCallback, 250)
      }
      else {
        onCompilingChangeRef.current?.(false)
      }
    })

    const unsubscribeB = codeFileB.subscribe(() => {
      const newCode = codeFileB.value
      // Use direct comparison first, then normalized for whitespace-only changes
      const codeChanged = lastSeenCodeB.current !== newCode
      const normalizedNewCode = normalizeWhitespace(newCode)
      const normalizedLastSeen = normalizeWhitespace(lastSeenCodeB.current)
      const normalizedChanged = normalizedLastSeen !== normalizedNewCode

      // If code hasn't actually changed (either directly or after normalization), cancel any pending compiles and return
      if (!codeChanged && !normalizedChanged) {
        if (compileTimeoutRefB.current) {
          clearTimeout(compileTimeoutRefB.current)
          compileTimeoutRefB.current = null
          // Clear loading state if there was a pending compilation
          onCompilingChangeRef.current?.(false)
        }
        return
      }

      lastSeenCodeB.current = newCode

      // Compile immediately for widgets (no delay, no loading)
      // This ensures widgets are updated in sync with keystrokes
      compileForWidgets(codeFileA.value, newCode)

      // Debounce error checking to handle rapid changes (e.g., commenting multiple lines)
      // Cancel any pending error check and schedule a new one
      // Use a longer debounce to ensure it runs even with very rapid changes
      if (errorCheckTimeoutRefB.current) {
        clearTimeout(errorCheckTimeoutRefB.current)
      }
      errorCheckTimeoutRefB.current = setTimeout(() => {
        errorCheckTimeoutRefB.current = null
        // Always read the latest code values when the timeout fires
        // This ensures we check the final state even if code changed multiple times
        const latestCodeA = codeFileA.value
        const latestCodeB = codeFileB.value
        currentCheckForErrors(latestCodeA, latestCodeB)
      }, 100)

      // If codeFile just changed, reset the flag
      if (codeFileJustChangedRef.current) {
        codeFileJustChangedRef.current = false
      }

      // Auto-compile in 'key' mode when code changes
      // Read compileMode directly from store to ensure we have the latest value
      const currentCompileMode = getCompileMode().compileMode
      if (currentCompileMode === 'key') {
        // Increment epoch - this marks a new compilation request
        compileEpoch.current++
        const epoch = compileEpoch.current

        // Capture variables in closure
        const capturedCodeFileA = codeFileA
        const capturedCodeFileB = codeFileB
        const capturedHandleCompile = handleCompileRef.current
        const capturedOnCompilingChange = onCompilingChangeRef.current
        const capturedLastCompiledCodeA = lastCompiledCodeA
        const capturedLastCompiledCodeB = lastCompiledCodeB
        const capturedCompileEpoch = compileEpoch
        const capturedCompileModeRef = compileModeRef
        const capturedEffectGeneration = effectGeneration

        // Create timeout callback - never clear timeouts, just check epoch
        const timeoutCallbackB = () => {
          // Check if effect has re-run - if so, this compilation is stale
          if (currentGeneration !== capturedEffectGeneration.current) {
            capturedOnCompilingChange?.(false)
            return
          }
          // Check if compileMode has changed - if not 'key', clear loading and return
          if (capturedCompileModeRef.current !== 'key') {
            capturedOnCompilingChange?.(false)
            return
          }
          // Check if this compilation is still current
          if (epoch !== capturedCompileEpoch.current) {
            // A newer compilation has started, clear loading for this stale one
            capturedOnCompilingChange?.(false)
            return
          }
          // Set loading at the start of compilation
          capturedOnCompilingChange?.(true)
          // Safety timeout: clear loading after max duration to prevent it from getting stuck
          const safetyTimeout = setTimeout(() => {
            if (currentGeneration === capturedEffectGeneration.current && epoch === capturedCompileEpoch.current
              && capturedCompileModeRef.current === 'key')
            {
              // Only clear if this compilation is still current, effect hasn't re-run, and mode is still 'key'
              // Otherwise, a newer compilation, effect re-run, or mode change should have handled it
              capturedOnCompilingChange?.(false)
            }
          }, 10000) // 10 second max duration
          ;(async () => {
            try {
              // Check effect generation, compileMode and epoch at start of async code
              if (currentGeneration !== capturedEffectGeneration.current || capturedCompileModeRef.current !== 'key'
                || epoch !== capturedCompileEpoch.current)
              {
                // Clear loading state since compilation is stale, mode changed, or effect re-ran
                clearTimeout(safetyTimeout)
                capturedOnCompilingChange?.(false)
                return
              }
              // Always use the latest code, not the captured code
              const currentCodeA = capturedCodeFileA.value
              const currentCodeB = capturedCodeFileB.value

              if (!capturedHandleCompile) {
                // handleCompile not ready yet, clear loading and return
                clearTimeout(safetyTimeout)
                if (capturedCompileModeRef.current === 'key' && epoch === capturedCompileEpoch.current) {
                  capturedOnCompilingChange?.(false)
                }
                return
              }

              const startTime = Date.now()
              const minLoadingDuration = 300
              const result = capturedHandleCompile(currentCodeA, currentCodeB)
              // Check epoch again after compilation
              if (epoch !== capturedCompileEpoch.current) {
                // Clear loading state since this compilation is stale
                capturedOnCompilingChange?.(false)
                return
              }
              // Don't update lastSeenCode here - it's already updated in the subscription
              if (result.success) {
                // Check effect generation, compileMode and epoch before proceeding - if stale, clear loading and return immediately
                if (currentGeneration !== capturedEffectGeneration.current || capturedCompileModeRef.current !== 'key'
                  || epoch !== capturedCompileEpoch.current)
                {
                  clearTimeout(safetyTimeout)
                  capturedOnCompilingChange?.(false)
                  return
                }
                capturedLastCompiledCodeA.current = currentCodeA
                capturedLastCompiledCodeB.current = currentCodeB
                // Wait for program loading to complete if it was started
                if (result.loadingPromise) {
                  try {
                    await result.loadingPromise
                    // Check effect generation, compileMode and epoch again after loading
                    if (currentGeneration !== capturedEffectGeneration.current
                      || capturedCompileModeRef.current !== 'key' || epoch !== capturedCompileEpoch.current)
                    {
                      // Clear loading state since compilation is stale, mode changed, or effect re-ran
                      clearTimeout(safetyTimeout)
                      capturedOnCompilingChange?.(false)
                      return
                    }
                  }
                  catch {
                    // Error already handled in handleCompile
                    if (currentGeneration !== capturedEffectGeneration.current
                      || capturedCompileModeRef.current !== 'key' || epoch !== capturedCompileEpoch.current)
                    {
                      // Clear loading state since compilation is stale, mode changed, or effect re-ran
                      clearTimeout(safetyTimeout)
                      capturedOnCompilingChange?.(false)
                      return
                    }
                  }
                }
                // Check effect generation, compileMode and epoch again before scheduling timeout
                if (currentGeneration !== capturedEffectGeneration.current || capturedCompileModeRef.current !== 'key'
                  || epoch !== capturedCompileEpoch.current)
                {
                  clearTimeout(safetyTimeout)
                  capturedOnCompilingChange?.(false)
                  return
                }
                const elapsed = Date.now() - startTime
                const remaining = Math.max(0, minLoadingDuration - elapsed)
                setTimeout(() => {
                  // Final check before clearing loading
                  // Check effect generation, compileMode and epoch
                  clearTimeout(safetyTimeout)
                  if (currentGeneration !== capturedEffectGeneration.current || capturedCompileModeRef.current !== 'key'
                    || epoch === capturedCompileEpoch.current)
                  {
                    capturedOnCompilingChange?.(false)
                  }
                }, remaining)
              }
              else {
                // Compilation failed, clear loading state only if still current
                clearTimeout(safetyTimeout)
                if (currentGeneration === capturedEffectGeneration.current && capturedCompileModeRef.current === 'key'
                  && epoch === capturedCompileEpoch.current)
                {
                  capturedOnCompilingChange?.(false)
                }
              }
            }
            catch (err) {
              // Clear loading state on error if still current
              clearTimeout(safetyTimeout)
              if (currentGeneration === capturedEffectGeneration.current && capturedCompileModeRef.current === 'key'
                && epoch === capturedCompileEpoch.current)
              {
                capturedOnCompilingChange?.(false)
              }
            }
          })()
        }
        // Schedule timeout - never clear it, just let it fire and check epoch
        setTimeout(timeoutCallbackB, 250)
      }
      else {
        onCompilingChangeRef.current?.(false)
      }
    })

    return () => {
      unsubscribeA()
      unsubscribeB()
      // Don't clear compile timeouts here - let them complete even if effect re-runs
      // The compilation ID system and effect generation will handle stale compilations
      if (errorCheckTimeoutRefA.current) {
        clearTimeout(errorCheckTimeoutRefA.current)
      }
      if (errorCheckTimeoutRefB.current) {
        clearTimeout(errorCheckTimeoutRefB.current)
      }
      // Clear loading state when effect cleanup runs
      // The new effect instance will handle loading for new compilations
      onCompilingChangeRef.current?.(false)
    }
  }, [
    compileMode,
    codeFileA,
    codeFileB,
    channelMode,
    activeTabId,
    playingTabId,
    globalBpm,
    crossfade,
    getEditorInitiatedPlayback,
    checkForErrors, // Include checkForErrors in dependencies so effect re-runs when it changes
  ])

  return null
}
